import { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, StackProps, Typography, TypographyProps } from '@mui/material';
import { kebabCase } from 'lodash';

import { Flag, TextFieldProps, TextFieldWithDebounceProps } from '@components/base';
import { EnhancedTextField } from '@components/base';
import InputGrid from '@components/base/layout/InputGrid';
import { CasingInputHint } from '@components/forms';
import { LowerCaseSearchLanguage, LowerCaseSearchLanguages } from '@models/CountriesAndLanguages';
import { MultiLangText } from '@models/MultiLangText';
import { DefaultLanguage } from '@store/utils';
import { kebabCasePattern } from '@utils/stringUtils';

export type LocalizedFieldsetProps = Pick<
  TextFieldProps,
  'disabled' | 'maxLength' | 'multiline' | 'required'
> &
  Pick<TextFieldWithDebounceProps, 'delay'> & {
    errors?: Partial<MultiLangText>;
    /**
     * Used to generate `name` and `id` for the input fields.
     */
    fieldName?: string;
    /**
     * Used to generate `id` for the input fields.
     */
    fieldsetId?: string;
    headingProps?: TypographyProps;
    helperTexts?: Partial<MultiLangText>;
    /**
     * Common label for the fieldset
     */
    label?: string;
    onChangeValue: (value: string, languages: LowerCaseSearchLanguage[], slug?: string) => void;
    /**
     * If `undefined`, the slug field will not be shown.
     */
    slug?: string;
    slugError?: string;
    slugHelperText?: string;
    values: MultiLangText;
    wrapperProps?: StackProps;
  };

const LocalizedFieldset: FC<LocalizedFieldsetProps> = ({
  delay,
  disabled,
  errors,
  fieldName,
  fieldsetId,
  headingProps: { variant: headingVariant = 'h4', ...headingProps } = {},
  helperTexts,
  label,
  maxLength,
  multiline,
  onChangeValue,
  required,
  slug,
  slugError,
  slugHelperText,
  values,
  wrapperProps: { spacing = 1, ...wrapperProps } = {},
}) => {
  const { t } = useTranslation();

  const [copyValueFromDefaultLang, setCopyValueFromDefaultLang] = useState<
    Record<LowerCaseSearchLanguage, boolean>
  >(
    LowerCaseSearchLanguages.reduce(
      (acc, cur) => ({
        ...acc,
        [cur]: cur !== DefaultLanguage && values[cur] === values[DefaultLanguage],
      }),
      {} as Record<LowerCaseSearchLanguage, boolean>
    )
  );

  const [generateSlugFromDefaultLang, setGenerateSlugFromDefaultLang] = useState(
    slug === kebabCase(values[DefaultLanguage])
  );

  const id = useId();

  const handleToggleCheckbox = (checked: boolean, language: LowerCaseSearchLanguage) => {
    if (checked) onChangeValue(values[DefaultLanguage], [language]);
    setCopyValueFromDefaultLang(prev => ({ ...prev, [language]: checked }));
  };

  return (
    <Stack spacing={spacing} {...wrapperProps}>
      {label && (
        <Typography variant={headingVariant as TypographyProps['variant']} {...headingProps}>
          {label}
        </Typography>
      )}

      <InputGrid>
        {LowerCaseSearchLanguages.map(lang => (
          <EnhancedTextField
            aria-label={`${t('language', { context: lang })} ${label}`}
            CheckboxProps={
              lang !== DefaultLanguage
                ? {
                    onChange: (_, checked) => handleToggleCheckbox(checked, lang),
                    checked: copyValueFromDefaultLang[lang],
                    label: t('useValueFromLanguage', {
                      language: t('language', { context: DefaultLanguage }),
                    }),
                  }
                : undefined
            }
            delay={delay}
            disabled={disabled}
            error={!!errors?.[lang]}
            helperText={errors?.[lang] ?? helperTexts?.[lang]}
            id={[...(fieldsetId ? [fieldsetId] : []), fieldName, lang, id].join('__')}
            key={lang}
            label={
              <>
                <Box mr={1}>
                  <Flag country={lang} size={16} variant='standard' />
                </Box>
                {t('language', { context: lang })}
              </>
            }
            maxLength={maxLength}
            mode={lang === DefaultLanguage || !copyValueFromDefaultLang[lang] ? 'manual' : 'auto'}
            multiline={multiline}
            name={`${fieldName}_${lang}`}
            onChange={({ currentTarget: { value } }) => {
              const languages = [
                lang,
                ...(lang === DefaultLanguage
                  ? LowerCaseSearchLanguages.filter(l => l === lang || copyValueFromDefaultLang[l])
                  : []),
              ];

              if (lang === DefaultLanguage && generateSlugFromDefaultLang) {
                const newSlug = kebabCase(value);
                onChangeValue(value, languages, newSlug);
              } else onChangeValue(value, languages);
            }}
            required={required}
            value={values[lang]}
          />
        ))}

        {slug != null && (
          <EnhancedTextField
            aria-label={t('slug')}
            CheckboxProps={{
              onChange: (_, checked) => {
                setGenerateSlugFromDefaultLang(checked);
                if (checked) {
                  const newSlug = kebabCase(values[DefaultLanguage]);
                  onChangeValue('', [], newSlug);
                }
              },
              checked: generateSlugFromDefaultLang,
              label: t('generateSlugFromLanguage', {
                language: t('language', { context: DefaultLanguage }),
              }),
            }}
            delay={delay}
            disabled={disabled}
            error={!!slugError}
            helperText={slugError ?? slugHelperText}
            id={[...(fieldsetId ? [fieldsetId] : []), fieldName, slug, id].join('__')}
            label={t('slug')}
            mode={generateSlugFromDefaultLang ? 'auto' : 'manual'}
            name={`${fieldName}_slug`}
            onChange={({ currentTarget: { value } }) => onChangeValue('', [], value)}
            required={required}
            slotProps={{
              htmlInput: { pattern: kebabCasePattern },
              input: { endAdornment: <CasingInputHint casing='kebab' position='end' /> },
            }}
            value={slug}
          />
        )}
      </InputGrid>
    </Stack>
  );
};

export default LocalizedFieldset;
